import { SELECT, execute , UPDATE , INSERT } from "@/utils/database_module"
import { availableName } from "@/utils/functions";
import { Conflict, ConflictData, ConflictHistory, ConflictStatus, Order, Product, User } from "@/utils/types"
import { stat } from "fs";
import moment from "moment";
import { Message } from "postcss";


const conflicts = {
    
    
    getAll: async (filter: Filter, user_id?: number) => {
        const { searchQuery, selectedStatus, startDate, endDate, limit, order } = filter;

        // 1. Prepare SQL condition for filtering by conflict status
        const statusStr = Array.isArray(selectedStatus)
            ? `AND (${selectedStatus.map(status => `c.status = ${status}`).join(" OR ")})`
            : selectedStatus && selectedStatus !== 0 
            ? `AND c.status = ${selectedStatus}` 
            : "";

        // 2. Set LIMIT clause if limit is provided
        const limitStr = limit ? `LIMIT ${limit}` : "";

        // 3. Set ORDER BY clause (default to descending by date)
        const orderStr = order && order !== "" ? `ORDER BY ${order}` : 'ORDER BY c.date DESC';

        // 4. Main query to get all non-deleted conflicts with status, ordering, and limit
        const query = `SELECT * FROM conflicts c WHERE is_deleted = 0 ${statusStr} ${orderStr} ${limitStr}`;
        const conflicts = await execute(query) as Conflict[];

        // 5. Extract related order IDs from conflicts
        const conflictsOrdersIds = conflicts.map(conflict => conflict.order_id).join(", ");

        // 6. Fetch orders related to conflicts
        const orders = conflictsOrdersIds 
            ? await execute(`SELECT * FROM orders WHERE id IN (${conflictsOrdersIds})`) as Order[] 
            : [];

        // 7. Extract user IDs from orders
        const ordersUsersIds = orders.map(order => order?.user_id).join(", ");

        // 8. Fetch user data related to orders
        const users = ordersUsersIds 
            ? await execute(`
                SELECT id, first_name, last_name, email, phone_numbers, profile_pic, token, adress 
                FROM users 
                WHERE role = 'user' AND id IN (${ordersUsersIds})
            `) as any[] 
            : [];

        // 9. Parse user phone numbers if stored as JSON string
        for (const user of users) {
            user.phone_numbers = typeof user.phone_numbers === 'string' 
            ? JSON.parse(`${user.phone_numbers}`) 
            : user.phone_numbers;
        }

        // 10. Extract product IDs from orders
        const ordersProductIds = orders.map(order => order.product_id).join(", ");

        // 11. Fetch product data related to orders
        const products = ordersProductIds 
            ? await execute(`SELECT id, name, images FROM products WHERE id IN (${ordersProductIds})`) as Product[] 
            : [];

        // 12. Parse product names if stored as JSON string
        for (const product of products) {
            product.name = typeof product.name === 'string' 
            ? JSON.parse(`${product.name}`) 
            : product.name;
        }

        // 13. Load conflict status list and map it later
        const conflictStatuses = await SELECT("conflict_status", { isIndexed: true }) as ConflictStatus[];

        // 14. Enrich each conflict with user, product, and status info
        for (const conflict of conflicts) {
            // Attach readable status from status table
            conflict.status = conflictStatuses[conflict.status as number];

            // Attach user based on order
            const userId = orders.find(order => order.id === conflict.order_id)?.user_id;
            conflict.user = users.find(user => user.id === userId);

            // Attach product and parse images if needed
            const productId = orders.find(order => order.id === conflict.order_id)?.product_id;
            const product = products.find(product => product.id === productId);
            if (product) {
            conflict.product = product;
            conflict.product.images = typeof product.images === 'string' 
                ? JSON.parse(`${product.images}`) 
                : ["image-placeholder.png"];
            }
        }

        // 15. Filter conflicts based on:
        // - searchQuery (type, product name, user name)
        // - date range (startDate and endDate)
        const filteredConflicts = conflicts.filter((conflict) => {
            const filterByType = conflict?.type.toLowerCase().includes(searchQuery?.toLowerCase() ?? "");
            const filterByProductName = JSON.stringify(conflict?.product?.name)
            ?.toLowerCase()
            .includes(searchQuery?.toLowerCase() ?? "");
            const filterByUserName =
            conflict?.user?.first_name.toLowerCase().includes(searchQuery?.toLowerCase() ?? "") ||
            conflict?.user?.last_name.toLowerCase().includes(searchQuery?.toLowerCase() ?? "");

            const filterByStartDate = startDate
            ? moment(moment(conflict.date).format("YYYY-MM-DD")).isSameOrAfter(moment(startDate).format("YYYY-MM-DD"))
            : true;

            const filterByEndDate = endDate
            ? moment(moment(conflict.date).format("YYYY-MM-DD")).isSameOrBefore(moment(endDate).format("YYYY-MM-DD"))
            : true;

            return (filterByType || filterByUserName || filterByProductName) && filterByStartDate && filterByEndDate;
        });

        // 16. Return filtered results
        return filteredConflicts;
    },


   getUserConflicts: async (user_id: number, filter: Filter) => {
    const { selectedStatus, startDate, endDate } = filter;

    // Get all orders made by the user
    const orders = (await SELECT("orders", {
        conditions: [{ column: "user_id", operator: "=", value: parseInt(`${user_id}`) }],
    })) as Order[];

    // Extract the order IDs
    const orderIds = orders.map((order) => order.id).join(", ");

    // If a specific status is selected (and not "0" or NaN), filter by it
    let selectedStatusStr =
        !isNaN(selectedStatus) && parseInt(`${selectedStatus}`) != 0
        ? ` AND status = ${parseInt(`${selectedStatus}`)} `
        : "";

    // Fetch all conflicts related to the user's orders and apply status filter
    const conflicts = orderIds
        ? (await execute(
            `SELECT * FROM conflicts WHERE is_deleted=0 AND order_id IN (${orderIds}) ${selectedStatusStr} ORDER BY date DESC`
        )) as Conflict[]
        : [];

    // Find all product IDs for only the orders that have conflicts
    const filteredOrdersProductsIds = orders
        .filter((order) => conflicts.some((conflict) => conflict.order_id === order.id))
        .map((o) => o.product_id)
        .join(", ");

    // Fetch corresponding product details
    const products = filteredOrdersProductsIds
        ? (await execute(
            `SELECT id, name, images FROM products WHERE id IN (${filteredOrdersProductsIds})`
        )) as Product[]
        : [];

    // Get all possible conflict statuses (as a lookup array)
    const conflictStatuses = (await SELECT(`conflict_status`, { isIndexed: true })) as ConflictStatus[];

    // Enrich each conflict with its readable status and linked product
    for (const conflict of conflicts) {
        conflict.status = conflictStatuses[conflict.status as number];

        const productId = orders.find((order) => order.id === conflict.order_id)?.product_id;
        const product = products.find((product) => product.id === productId);

        if (product) {
        conflict.product = product;
        conflict.product.images =
            typeof product.images == "string"
            ? JSON.parse(`${product.images}`)
            : ["image-placeholder.png"];
        }
    }

    // Filter conflicts by date range
    const filteredConflicts = conflicts.filter((conflict) => {
        const filterByStartDate =
        startDate &&
        !moment(moment(conflict.date).format("YYYY-MM-DD")).isSameOrAfter(
            moment(startDate).format("YYYY-MM-DD")
        )
            ? false
            : true;

        const filterByEndDate =
        endDate &&
        !moment(moment(conflict.date).format("YYYY-MM-DD")).isSameOrBefore(
            moment(endDate).format("YYYY-MM-DD")
        )
            ? false
            : true;

        return filterByStartDate && filterByEndDate;
    });

    // Return the final list of enriched and filtered conflicts
    return filteredConflicts;
    },

    update : async (id : number , conflict : { status : number , details : string , history : ConflictHistory}) => {
        const { status , details , history } = conflict
        await UPDATE("conflicts" , { status : status , details : details } , [{column : "id" , operator : "=" , value : id}])
        
        const [newStatus] = await SELECT("conflict_status" , { conditions : [{column : "id" , operator : "=" , value : status }]}) as ConflictStatus[]
        await INSERT("conflicts_history" , { conflict_id : id , user_id : history.user_id , action : history.action , details : {...history.details , new_status : newStatus} })
    },
    
    insert : async (data : ConflictData & Message) => {
        const conflictToAdd = { type: data.type, order_id: data.order_id, date: data.date, status: data.status,}
        const conflict = await INSERT("conflicts" , conflictToAdd)
        await INSERT("chat_messages" , {
            message: data.message,
            image: data.image,
            chat_id: `${conflict.id}`,
            is_conflict : 1,
            sender_id: data.sender_id,
            user_id : data.user_id
        });
        await INSERT("conflicts_history" , {
            conflict_id: conflict.id,
            user_id: data.user_id,
            action: "Création du conflit",
            date: moment().format("YYYY-MM-DD HH:mm:ss"),
        });
        return conflict

    },

    getHistory : async (id : number) => {
        const history = await SELECT("conflicts_history" , { conditions : [{column : "conflict_id" , operator : "=" , value : id }] }) as ConflictHistory[];
        
        const usersIds = history.map((data) => data.user_id);
        const users = usersIds.length>0 ? await execute( `SELECT first_name , last_name , role ,profile_pic,email, token, id FROM users WHERE id IN (${usersIds.join(",")})`) as User[]: [] as User[];
        
        for (let i = 0; i < history.length; i++) {
            const historyItem = history[i];
            const user = users?.find((user) => user.id == historyItem.user_id);
            history[i] = {...historyItem , user : user}
        }
       
        return history
    },

    delete : async (id : number) => {
        return await UPDATE("conflicts" , { is_deleted : 1 } , [{column : "id" , operator : "=" , value : id}])
    }

}

export default conflicts



type Filter = {
    startDate?: string;
    endDate?: string;
    selectedStatus?: any ;
    searchQuery?: string;
    limit?: number;
    order?: string;
  };